import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, BrainCircuit, Send, Loader2, MapPin, Stethoscope, Baby, Zap, Sparkles,
  Boxes, FileCode2, CornerDownRight, Wand2, Network,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const SUB_COLORS = {
  entities: "#7c3aed", components: "#3b9eff", scenes: "#22c55e", behaviours: "#f59e0b",
  logic: "#ef4444", physics: "#06b6d4", materials: "#ec4899", animations: "#a855f7",
  audio: "#14b8a6", ai: "#f43f5e", networking: "#0ea5e9", ui: "#84cc16", quests: "#eab308",
  inventory: "#fb923c", interactions: "#38bdf8", triggers: "#facc15", meta: "#94a3b8", source: "#e2e8f0",
};

// Radial dependency-graph: subsystems clustered around a circle, nodes within, edges as faint lines.
function GraphView({ graph }) {
  const [hover, setHover] = useState(null);
  const layout = React.useMemo(() => {
    const nodes = graph?.nodes || [];
    const W = 820, H = 560, cx = W / 2, cy = H / 2, R = 205;
    const subs = [...new Set(nodes.map((n) => n.subsystem))];
    const bySub = {};
    nodes.forEach((n) => { (bySub[n.subsystem] = bySub[n.subsystem] || []).push(n); });
    const pos = {};
    subs.forEach((s, i) => {
      const a = (i / subs.length) * Math.PI * 2 - Math.PI / 2;
      const gx = cx + R * Math.cos(a), gy = cy + R * Math.sin(a);
      const list = bySub[s];
      const rr = Math.min(60, 14 + list.length * 4);
      list.forEach((n, j) => {
        if (list.length === 1) { pos[n.id] = { x: gx, y: gy, s }; return; }
        const aa = (j / list.length) * Math.PI * 2;
        pos[n.id] = { x: gx + rr * Math.cos(aa), y: gy + rr * Math.sin(aa), s };
      });
    });
    return { W, H, pos, edges: graph?.edges || [] };
  }, [graph]);

  if (!graph) return null;
  const { W, H, pos, edges } = layout;
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 overflow-hidden" data-testid="brain-graph">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: "62vh" }}>
        {edges.map((e, i) => {
          const a = pos[e.from], b = pos[e.to];
          if (!a || !b) return null;
          const on = hover && (e.from === hover || e.to === hover);
          return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={on ? "#3b9eff" : "#ffffff"} strokeOpacity={on ? 0.5 : 0.08} strokeWidth={on ? 1.4 : 0.7} />;
        })}
        {Object.entries(pos).map(([id, p]) => {
          const n = graph.nodes.find((x) => x.id === id);
          const col = SUB_COLORS[p.s] || "#94a3b8";
          const on = hover === id;
          return (
            <g key={id} onMouseEnter={() => setHover(id)} onMouseLeave={() => setHover(null)} style={{ cursor: "pointer" }}>
              <circle cx={p.x} cy={p.y} r={on ? 7 : n?.kind === "code" ? 4.5 : 4} fill={col} fillOpacity={on ? 1 : 0.85} stroke={on ? "#fff" : "none"} strokeWidth={1} />
              {on && <text x={p.x + 9} y={p.y + 3} fill="#fff" fontSize="11" style={{ paintOrder: "stroke", stroke: "#000", strokeWidth: 3 }}>{n?.title?.slice(0, 42)}</text>}
            </g>
          );
        })}
      </svg>
      <div className="flex flex-wrap gap-2 p-3 border-t border-white/10" data-testid="brain-graph-legend">
        {[...new Set((graph.nodes || []).map((n) => n.subsystem))].map((s) => (
          <span key={s} className="inline-flex items-center gap-1 text-[10px] text-white/55">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: SUB_COLORS[s] || "#94a3b8" }} /> {s}
          </span>
        ))}
      </div>
    </div>
  );
}

const VERBS = [
  { id: "locate", label: "Locate", Icon: MapPin, hint: "Where is … controlled?" },
  { id: "diagnose", label: "Diagnose", Icon: Stethoscope, hint: "Why doesn't … work?" },
  { id: "explain12", label: "Explain (age 12)", Icon: Baby, hint: "Explain this simply" },
  { id: "optimise", label: "Optimise", Icon: Zap, hint: "Make it run better" },
];

function CiteBadge({ c }) {
  const isCode = c.includes(".") && c.includes(":");
  const Icon = isCode ? FileCode2 : Boxes;
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-[#7c3aed]/15 border border-[#7c3aed]/40 px-1.5 py-0.5 text-[10px] font-mono text-[#c4b5fd]">
      <Icon className="w-3 h-3" /> {c}
    </span>
  );
}

export default function CodeBrain() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(null);   // {meta, stats, editable}
  const [thread, setThread] = useState([]);    // {role, text, sources?, citations?, action?}
  const [q, setQ] = useState("");
  const [intent, setIntent] = useState("locate");
  const [busy, setBusy] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [graph, setGraph] = useState(null);
  const endRef = useRef(null);

  const loadGraph = useCallback(async () => {
    try { const { data } = await api.get(`/codebrain/${gameId}/graph`); setGraph(data); }
    catch { setGraph({ nodes: [], edges: [] }); }
  }, [gameId]);

  useEffect(() => {
    api.get(`/codebrain/${gameId}/index`)
      .then((r) => setIndex(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open Code Brain."))
      .finally(() => setLoading(false));
  }, [gameId]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [thread, busy]);

  const ask = useCallback(async (queryText, useIntent) => {
    const query = (queryText ?? q).trim();
    if (!query || busy) return;
    setThread((t) => [...t, { role: "user", text: query }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post(`/codebrain/${gameId}/ask`, { query, intent: useIntent ?? intent }, { timeout: 90000 });
      setThread((t) => [...t, { role: "brain", text: data.answer, sources: data.sources, citations: data.used_citations, action: data.action }]);
    } catch (e) {
      setThread((t) => [...t, { role: "brain", text: formatApiError(e.response?.data?.detail) || "I hit a snag — try rephrasing.", error: true }]);
    } finally { setBusy(false); }
  }, [q, intent, busy, gameId]);

  const applyingRef = useRef(false);
  const applyChange = (instruction) => {
    if (applyingRef.current) return;
    applyingRef.current = true;
    try { sessionStorage.setItem("gv_pending_edit", instruction); } catch {}
    toast.success("Opening the builder with this change ready…");
    setTimeout(() => navigate(`/app/build/${gameId}`), 700);
  };

  if (loading) return <Spinner />;

  const stats = index?.stats || {};

  return (
    <div className="min-h-screen bg-[#070912] text-white flex flex-col" data-testid="code-brain">
      <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col p-5 lg:p-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="brain-back" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex items-center gap-2 min-w-0">
            <BrainCircuit className="w-6 h-6 text-[#7c3aed]" />
            <h1 className="font-display text-xl lg:text-2xl font-extrabold tracking-tight truncate">Code Brain</h1>
          </div>
          <span data-testid="brain-index-stat" className="ml-auto rounded-full bg-white/5 border border-white/10 px-3 py-1 text-xs font-mono text-white/60">
            {stats.total || 0} nodes · {stats.ir || 0} IR · {stats.code || 0} source
          </span>
          <button onClick={() => { const n = !showGraph; setShowGraph(n); if (n && !graph) loadGraph(); }} data-testid="brain-graph-toggle"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${showGraph ? "border-[#7c3aed] bg-[#7c3aed]/20 text-white" : "border-white/10 bg-white/5 text-white/70 hover:bg-white/10"}`}>
            <Network className="w-3.5 h-3.5" /> Graph
          </button>
        </div>
        <p className="text-sm text-white/45 mb-4">
          Ask about <span className="text-white/70">{index?.meta?.title}</span>. Code Brain finds the relevant part of your
          game first, then answers with citations. {stats.files?.length ? `Indexed source: ${stats.files.join(", ")}.` : "No imported source — indexing the Game IR."}
        </p>

        {/* Verb chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="brain-verbs">
          {VERBS.map((v) => (
            <button key={v.id} data-testid={`brain-verb-${v.id}`} onClick={() => setIntent(v.id)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${intent === v.id ? "border-[#7c3aed] bg-[#7c3aed]/20 text-white" : "border-white/15 bg-white/5 text-white/70 hover:bg-white/10"}`}>
              <v.Icon className="w-3.5 h-3.5" /> {v.label}
            </button>
          ))}
        </div>

        {/* Dependency graph */}
        {showGraph && (
          <div className="mb-4" data-testid="brain-graph-panel">
            {graph ? (
              graph.nodes?.length ? <GraphView graph={graph} /> : <p className="text-sm text-white/40">No graph nodes.</p>
            ) : (
              <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Mapping your game's systems…</div>
            )}
            <p className="text-[10px] text-white/30 text-center mt-2">Every dot is a system in your game; lines show how they connect. Hover to see what each is.</p>
          </div>
        )}

        {/* Thread */}
        <div className="flex-1 space-y-4 overflow-y-auto pb-4" data-testid="brain-thread">
          {thread.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 text-sm text-white/60" data-testid="brain-empty">
              <p className="font-semibold text-white/80 mb-2">Try asking:</p>
              <div className="space-y-1.5">
                {["Where is the win condition controlled?", "Why isn't my enemy moving?", "Explain the physics like I'm 12", "Make the player move faster"].map((s) => (
                  <button key={s} onClick={() => ask(s, intent)} className="block text-left text-[#93c5fd] hover:text-white transition-colors" data-testid="brain-suggestion">
                    <CornerDownRight className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {thread.map((m, i) => (
            <div key={i} data-testid={`brain-msg-${i}`} className={m.role === "user" ? "flex justify-end" : ""}>
              {m.role === "user" ? (
                <div className="max-w-[85%] rounded-2xl rounded-br-md bg-[#7c3aed] text-white px-4 py-2.5 text-sm">{m.text}</div>
              ) : (
                <div className={`max-w-[92%] rounded-2xl rounded-bl-md border px-4 py-3 text-sm ${m.error ? "border-rose-500/40 bg-rose-500/10 text-rose-200" : "border-white/10 bg-white/[0.04] text-white/85"}`}>
                  <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
                  {m.citations?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`brain-citations-${i}`}>
                      {m.citations.map((c) => <CiteBadge key={c} c={c} />)}
                    </div>
                  )}
                  {m.action && (
                    <Button onClick={() => applyChange(m.action.instruction)} data-testid={`brain-apply-${i}`}
                      className="mt-3 rounded-full bg-gradient-to-r from-emerald-500 to-[#3b9eff] hover:opacity-90 text-white h-8 text-xs">
                      <Wand2 className="w-3.5 h-3.5" /> Apply: {m.action.summary || "make this change"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
          {busy && (
            <div className="flex items-center gap-2 text-white/50 text-sm" data-testid="brain-thinking">
              <Loader2 className="w-4 h-4 animate-spin text-[#7c3aed]" /> Finding the relevant part of your game…
            </div>
          )}
          <div ref={endRef} />
        </div>

        {/* Composer */}
        <div className="sticky bottom-0 bg-[#070912] pt-2">
          <div className="flex items-end gap-2 rounded-2xl border border-white/15 bg-white/[0.04] p-2">
            <textarea
              data-testid="brain-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
              rows={1} placeholder={VERBS.find((v) => v.id === intent)?.hint || "Ask about your game…"}
              className="flex-1 resize-none bg-transparent outline-none text-sm text-white placeholder:text-white/35 px-2 py-2 max-h-32" />
            <Button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="brain-send"
              className="rounded-xl bg-[#7c3aed] hover:bg-[#6d28d9] text-white shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-[10px] text-white/30 text-center mt-1.5">Grounded in your Game IR{stats.code ? " + imported source" : ""} · answers cite their sources</p>
        </div>
      </div>
    </div>
  );
}
